/**
 * Multi-stop route optimizer.
 *
 * Given the driver's current GPS + a list of customer destinations,
 * computes the optimal delivery sequence (TSP — Travelling Salesman
 * Problem variant).
 *
 * Primary: OSRM /trip endpoint — the canonical free TSP solver.
 *   https://router.project-osrm.org/trip/v1/driving/{coords}
 *     ?source=first&roundtrip=false&overview=full&geometries=geojson
 *
 *   - `source=first`     → driver's current GPS is the fixed start
 *   - `roundtrip=false`  → no return-to-origin (one-way delivery run)
 *   - The response gives each waypoint a `waypoint_index` field
 *     indicating its position in the OPTIMAL sequence.
 *
 * Fallback: Nearest-Neighbor heuristic (greedy). Suboptimal but works
 * offline / when OSRM is unreachable. Within ~15-20% of optimal for
 * small N (3-9 stops), which is the realistic delivery-run size.
 *
 * Limits: Google Maps mobile-app deep-link supports up to 9 waypoints
 * + 1 destination (10 stops total). If the driver has more, we
 * optimize the full set then truncate to the first 10 — they can
 * re-run the optimizer after completing the first batch.
 */

import { haversineKm } from './navigateTo';

const MAX_GMAPS_STOPS = 10;

/**
 * @typedef {Object} Stop
 * @property {string} id        Stable identifier (we use order id)
 * @property {number} lat
 * @property {number} lng
 * @property {string} [label]
 */

/**
 * Build the OSRM /trip URL and call it.
 * Returns `{ ordered: Stop[], distance: meters, duration: seconds }`
 * or `null` if the call fails (caller should fall back to NN).
 */
const tripFromOsrm = async (origin, stops) => {
  const all = [origin, ...stops];
  const coords = all.map((p) => `${p.lng},${p.lat}`).join(';');
  const url =
    `https://router.project-osrm.org/trip/v1/driving/${coords}` +
    `?source=first&roundtrip=false&overview=false`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`OSRM /trip HTTP ${res.status}`);
  const data = await res.json();
  const trip = data?.trips?.[0];
  const waypoints = data?.waypoints;
  if (!trip || !Array.isArray(waypoints) || waypoints.length !== all.length) {
    throw new Error('OSRM /trip malformed response');
  }
  // waypoint[i].waypoint_index = position of original input[i] in the
  // optimal sequence. Skip index 0 (driver origin).
  const ordered = new Array(stops.length);
  for (let i = 1; i < waypoints.length; i += 1) {
    const pos = waypoints[i].waypoint_index;
    // pos is 0-based in the optimal sequence INCLUDING the driver origin
    // at position 0; subtract 1 to get the position in the stops array.
    ordered[pos - 1] = stops[i - 1];
  }
  return {
    ordered: ordered.filter(Boolean),
    distance: trip.distance,
    duration: trip.duration,
    source: 'osrm',
  };
};

/**
 * Greedy nearest-neighbor TSP. Starts at `origin`, repeatedly jumps to
 * the closest unvisited stop. Distance via Haversine (great-circle).
 */
const tripNearestNeighbor = (origin, stops) => {
  const remaining = [...stops];
  const ordered = [];
  let curr = origin;
  let totalKm = 0;
  while (remaining.length) {
    let bestIdx = 0;
    let bestKm = Infinity;
    for (let i = 0; i < remaining.length; i += 1) {
      const km = haversineKm(curr, remaining[i]);
      if (km !== null && km < bestKm) {
        bestKm = km;
        bestIdx = i;
      }
    }
    const next = remaining.splice(bestIdx, 1)[0];
    ordered.push(next);
    totalKm += bestKm;
    curr = next;
  }
  return {
    ordered,
    distance: totalKm * 1000, // meters, to match OSRM
    duration: null,
    source: 'nearest-neighbor',
  };
};

/**
 * Public entry point. Always resolves (never rejects) — uses NN if OSRM
 * fails so the UI never blocks waiting for an external service.
 *
 * @param {Stop} origin       Driver's current GPS
 * @param {Stop[]} stops      Customer destinations
 * @returns {Promise<{ordered:Stop[], distance:number, duration:number|null, source:'osrm'|'nearest-neighbor'}>}
 */
export const optimizeTrip = async (origin, stops) => {
  if (!origin || !Array.isArray(stops) || stops.length === 0) {
    return { ordered: [], distance: 0, duration: 0, source: 'nearest-neighbor' };
  }
  if (stops.length === 1) {
    const km = haversineKm(origin, stops[0]) || 0;
    return { ordered: [stops[0]], distance: km * 1000, duration: null, source: 'nearest-neighbor' };
  }
  try {
    return await tripFromOsrm(origin, stops);
  } catch {
    return tripNearestNeighbor(origin, stops);
  }
};

export { MAX_GMAPS_STOPS };
